import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { User, Building2, Loader2, Eye, EyeOff, HelpCircle, Mail, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { showIntercomAnonymous } from '@/components/IntercomProvider';

const registerSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  organizationName: z.string().min(1, 'Business name is required'),
});

type RegisterForm = z.infer<typeof registerSchema>;

export default function Register() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmationEmail, setConfirmationEmail] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterForm) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await signUp(data.email, data.password, {
        first_name: data.firstName,
        last_name: data.lastName,
        organization_name: data.organizationName,
      });

      if ((result as any).needsEmailConfirmation) {
        // Email confirmation required -- show check-email screen
        setConfirmationEmail(data.email);
      } else {
        // Instant access -- go to onboarding
        navigate('/onboarding');
      }
    } catch (err: unknown) {
      const error = err as { message?: string };
      setError(error.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  // Email confirmation screen
  if (confirmationEmail) {
    return (
      <div className="min-h-screen flex flex-col bg-zinc-100 dark:bg-zinc-950">
        <header className="bg-zinc-950 dark:bg-black text-white px-6 py-3 flex items-center gap-2 border-b border-zinc-900">
          <img src="/blazepos-logo.png" alt="BlazePOS" className="h-6 w-6 object-contain" />
          <span className="font-semibold text-lg tracking-tight">BlazePOS</span>
        </header>

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="max-w-md w-full text-center">
            <div className="w-16 h-16 rounded-2xl bg-blue-100 dark:bg-blue-950/30 flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
              Check your email
            </h1>
            <p className="text-zinc-500 dark:text-zinc-400 mb-2">
              We sent a confirmation link to
            </p>
            <p className="font-medium text-zinc-900 dark:text-white mb-6">
              {confirmationEmail}
            </p>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-8">
              Click the link in the email to activate your account and start your 14-day free trial.
              The link will take you straight to your dashboard.
            </p>
            <div className="space-y-3">
              <Button
                variant="outline"
                onClick={() => setConfirmationEmail(null)}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to registration
              </Button>
              <p className="text-xs text-zinc-400 dark:text-zinc-500">
                Didn't get it? Check your spam folder or try registering again.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-zinc-100 dark:bg-zinc-950">
      {/* Header Bar */}
      <header className="bg-zinc-950 dark:bg-black text-white px-6 py-3 flex items-center justify-between border-b border-zinc-900">
        <div className="flex items-center gap-2">
          <img src="/blazepos-logo.png" alt="BlazePOS" className="h-6 w-6 object-contain" />
          <span className="font-semibold text-lg tracking-tight">BlazePOS</span>
        </div>
        <button
          type="button"
          onClick={showIntercomAnonymous}
          className="text-sm text-zinc-300 hover:text-white transition-colors flex items-center gap-1"
        >
          <HelpCircle className="h-4 w-4" />
          Help
        </button>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Left Side - Register Form */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16 bg-white dark:bg-zinc-900">
          <div className="w-full max-w-md">
            <div className="mb-8">
              <h1 className="text-2xl font-semibold text-zinc-900 dark:text-white mb-2">
                Create your account
              </h1>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Start your 14-day free trial. No credit card required.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    First name
                  </Label>
                  <div className="relative">
                    <Input
                      id="firstName"
                      placeholder="John"
                      className="h-11 px-4 pr-10 bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:border-primary focus:ring-primary"
                      {...register('firstName')}
                    />
                    <User className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-500" />
                  </div>
                  {errors.firstName && (
                    <p className="text-sm text-red-600 dark:text-red-400">{errors.firstName.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastName" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                    Last name
                  </Label>
                  <Input
                    id="lastName"
                    placeholder="Doe"
                    className="h-11 px-4 bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:border-blue-500 focus:ring-blue-500"
                    {...register('lastName')}
                  />
                  {errors.lastName && (
                    <p className="text-sm text-red-600 dark:text-red-400">{errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="organizationName" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Business name
                </Label>
                <div className="relative">
                  <Input
                    id="organizationName"
                    placeholder="My Business"
                    className="h-11 px-4 pr-10 bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:border-blue-500 focus:ring-blue-500"
                    {...register('organizationName')}
                  />
                  <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 dark:text-zinc-500" />
                </div>
                {errors.organizationName && (
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.organizationName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  className="h-11 px-4 bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:border-blue-500 focus:ring-blue-500"
                  {...register('email')}
                />
                {errors.email && (
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 8 characters"
                    className="h-11 px-4 pr-10 bg-white dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-900 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:border-blue-500 focus:ring-blue-500"
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:hover:text-zinc-300"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.password.message}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full h-11 font-medium rounded-lg transition-colors"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating account...
                  </>
                ) : (
                  'Create account'
                )}
              </Button>
            </form>

            <p className="mt-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
              Already have an account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline">
                Log in
              </Link>
            </p>

            <p className="mt-4 text-center text-xs text-zinc-400 dark:text-zinc-500">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="underline hover:text-zinc-600 dark:hover:text-zinc-300">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="underline hover:text-zinc-600 dark:hover:text-zinc-300">
                Privacy Policy
              </Link>
            </p>
          </div>
        </div>

        {/* Right Side - Hero */}
        <div className="hidden lg:flex lg:w-1/2 blaze-gradient items-center justify-center p-12 relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl" />
          </div>

          {/* Content */}
          <div className="relative z-10 text-center">
            {/* POS Terminal Illustration */}
            <div className="relative mx-auto w-80 h-80 mb-8">
              {/* Monitor */}
              <div className="absolute inset-x-4 top-0 h-56 bg-zinc-900 rounded-2xl shadow-2xl border-4 border-zinc-800 overflow-hidden">
                {/* Screen */}
                <div className="m-3 h-44 bg-zinc-800 rounded-lg overflow-hidden">
                  {/* Mock POS Interface */}
                  <div className="p-2 h-full flex">
                    {/* Sidebar */}
                    <div className="w-8 bg-zinc-700/50 rounded mr-2 flex flex-col gap-1 p-1">
                      <div className="w-full h-4 bg-orange-500/80 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                      <div className="w-full h-4 bg-zinc-600 rounded" />
                    </div>
                    {/* Main content */}
                    <div className="flex-1 grid grid-cols-3 gap-1">
                      <div className="bg-purple-500/60 rounded" />
                      <div className="bg-amber-500/60 rounded" />
                      <div className="bg-green-500/60 rounded" />
                      <div className="bg-blue-500/60 rounded" />
                      <div className="bg-rose-500/60 rounded" />
                      <div className="bg-cyan-500/60 rounded" />
                      <div className="bg-indigo-500/60 rounded" />
                      <div className="bg-orange-500/60 rounded" />
                      <div className="bg-teal-500/60 rounded" />
                    </div>
                  </div>
                </div>
              </div>
              {/* Stand */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-20 h-16 bg-zinc-800 rounded-b-xl" />
              {/* Base */}
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-32 h-4 bg-zinc-900 rounded-full shadow-lg" />
            </div>

            <h2 className="text-2xl font-semibold text-white mb-3">
              Start selling in minutes
            </h2>
            <p className="text-white/80 text-lg max-w-sm mx-auto">
              Set up your POS system and start processing transactions with a 14-day free trial
            </p>

            {/* Feature badges */}
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                No credit card
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                14-day trial
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Cancel anytime
              </span>
              <span className="px-3 py-1.5 bg-white/20 rounded-full text-white text-sm backdrop-blur-sm">
                Full access
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-zinc-50 dark:bg-zinc-800 border-t border-zinc-200 dark:border-zinc-700 px-6 py-4">
        <div className="flex items-center justify-between text-sm text-zinc-500 dark:text-zinc-400">
          <p>&copy; {new Date().getFullYear()} BlazePOS. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <a href="https://www.blazepointofsale.com/privacy" className="hover:text-zinc-700 dark:hover:text-zinc-300">
              Privacy Policy
            </a>
            <a href="https://www.blazepointofsale.com/terms" className="hover:text-zinc-700 dark:hover:text-zinc-300">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
